import { useQuery } from '@tanstack/react-query';
import { placementPath, type Board, type BoardPlacement, type Navigation, type NavArea } from '@kaba/shared';
import { api, ApiError } from './api';
import { queryClient } from './board';
import { toast } from './toast';

export const navKey = ['nav'] as const;

/** Bereiche und Ordner der angemeldeten Person (nur Struktur, keine Boardrechte) */
export function useNav(enabled = true) {
  return useQuery({ queryKey: navKey, queryFn: () => api.get<Navigation>('/api/nav'), enabled, staleTime: 30_000 });
}

export function refreshStructure(): Promise<void> {
  void queryClient.invalidateQueries({ queryKey: ['me'] });
  const nav = queryClient.invalidateQueries({ queryKey: navKey });
  void queryClient.invalidateQueries({ queryKey: ['boards'] });
  void queryClient.invalidateQueries({ queryKey: ['boards-archived'] });
  // Übersichten: Konfiguration (andere Geräte) und Daten (Zugriff geändert)
  void queryClient.invalidateQueries({ queryKey: ['dashboards'] });
  void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  void queryClient.invalidateQueries({ queryKey: ['dash-data'] });
  return nav;
}

/** Ziel einer Ablage */
export interface Target {
  areaId: string | null;
  folderId: string | null;
}

export function targetPath(nav: Navigation | undefined, t: Target): string {
  if (!t.areaId) return 'Nicht einsortiert';
  const a = nav?.areas.find((x) => x.id === t.areaId);
  if (!a) return 'Unbekannter Bereich';
  const f = t.folderId ? a.folders.find((x) => x.id === t.folderId) : null;
  return f ? `${a.name} / ${f.name}` : a.name;
}

export const samePlace = (p: BoardPlacement | undefined, t: Target) =>
  (p?.kind === 'none' && !t.areaId) || (p?.kind === 'visible' && p.areaId === t.areaId && (p.folderId ?? null) === t.folderId);

/** Mögliche Ziele für ein Board: Bereiche desselben Teams und ihre Ordner (B02) */
export function targetsFor(nav: Navigation | undefined, teamId: string | null | undefined): NavArea[] {
  if (!teamId) return [];
  return (nav?.areas ?? []).filter((a) => a.teamId === teamId);
}

/**
 * Board verschieben – bestätigte Serveroperation mit erwarteter Version (M05). Danach der neue Pfad
 * mit „Rückgängig“ (B02); Rückgängig ist wieder eine geprüfte Verschiebung und überschreibt keine
 * spätere fremde Änderung.
 */
export async function moveBoard(
  board: Pick<Board, 'id' | 'title'> & { placement?: BoardPlacement },
  target: Target,
  opts: { onConflict?: () => void; undo?: boolean } = {},
): Promise<Board | null> {
  const before = board.placement;
  try {
    const moved = await api.put<Board>(`/api/boards/${board.id}/placement`, { ...target, expectedVersion: before?.version ?? 0 });
    refreshStructure();
    void queryClient.invalidateQueries({ queryKey: ['board', board.id] });
    const path = placementPath(moved.placement);
    if (opts.undo) toast.info(`„${board.title}“ liegt wieder unter ${path}.`);
    else if (before && before.kind !== 'hidden') {
      const back: Target = { areaId: before.areaId, folderId: before.folderId };
      toast.info(`„${board.title}“ liegt jetzt unter ${path}. Zugriffsrechte bleiben unverändert.`, {
        label: 'Rückgängig',
        run: () => void moveBoard({ ...board, placement: moved.placement }, back, { ...opts, undo: true }),
      });
    } else toast.info(`„${board.title}“ liegt jetzt unter ${path}.`);
    return moved;
  } catch (err) {
    refreshStructure();
    if (err instanceof ApiError && (err.status === 409 || err.status === 404) && opts.onConflict) {
      toast.error(`${(err as Error).message}`);
      opts.onConflict();
    } else toast.error(`Nicht verschoben: ${(err as Error).message}`);
    return null;
  }
}
